<template>
  <div class="ticket-price-list-mobile">
    <v-expansion-panels v-model="panels">
      <v-expansion-panel
        v-for="(config, index) of sortedTicketConfig"
        :key="index"
        class="elevation-0 mb-4"
      >
        <div
          v-if="!configIsConditional(config) && displaySaleEndTimer(config)"
          class="sale-timer"
        >
          <b-sale-end-timer
            :color="$whitelabel.lightTheme.textPrimary.lighten5"
            :countdown-end="config.endSaleAt"
          ></b-sale-end-timer>
        </div>
        <div v-if="configIsConditional(config)" class="conditional-box">
          <span class="MOBILE_h11_Bold">{{
            `${$t('tickets.availableWith')} ${conditionalConfigText(config)}`
          }}</span>
        </div>
        <v-expansion-panel-header>
          <div class="d-flex flex-column">
            <p class="font-weight-bold mb-1">
              {{ config.name }}
              <v-icon
                size="2rem"
                class="icon-styling px-0 pb-1 pl-1"
                :disabled="!config.description"
                @click.native.stop="showTicketInfoDialog(config)"
              >
                mdi-information
              </v-icon>
            </p>
            <p
              class="small-2 mb-0"
              :class="{
                'sold-out':
                  isSoldOut(config) ||
                  isCanceledEvent ||
                  timeslotSoldOut ||
                  !ticketConfigIsSelectable(config)
              }"
            >
              {{ ticketAvailabilityLevel(config) }}
            </p>
          </div>
          <template #actions>
            <v-icon size="16px"> $chevronDown </v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div
            v-for="(discount, discountIndex) of config.discounts"
            :key="index + '_' + discountIndex"
          >
            <p class="font-weight-bold d-flex justify-space-between mb-0">
              <span>{{ discount.name }}:</span>
              <span
                >{{ discount.finalPrice.toFixed(2) }}
                {{ config.currency }}</span
              >
            </p>
            <v-row no-gutters class="mt-2">
              <v-col class="d-flex justify-center flex-column">
                <p
                  v-if="discount.fees > 0"
                  class="small-2 mb-1 text-capitalize"
                >
                  {{ $t('cart.fees') }}:
                  {{ (discount.fees * discount.selectedQuantity).toFixed(2) }}
                  {{ config.currency }}
                </p>
                <p class="small-2 text-capitalize mb-0">
                  {{ $t('total') }}:
                  {{
                    (
                      (discount.finalPrice + discount.fees) *
                      discount.selectedQuantity
                    ).toFixed(2)
                  }}
                  {{ config.currency }}
                </p>
              </v-col>
              <v-col
                v-if="discountAvailable(discount)"
                cols="auto"
                class="d-flex align-center"
              >
                <v-btn
                  :outlined="discount.selectedQuantity === 0 || isCanceledEvent"
                  class="add-cart-button"
                  depressed
                  height="32px"
                  width="34px"
                  elevation="0"
                  :disabled="discount.selectedQuantity === 0 || isCanceledEvent"
                  @click="$emit('remove', { ticket: config, discount })"
                >
                  <v-icon size="10px">$minus</v-icon>
                </v-btn>
                <p class="selected-quantity text-center mb-0">
                  {{
                    discount.selectedQuantity > 0
                      ? discount.selectedQuantity
                      : '-'
                  }}
                </p>
                <v-btn
                  class="add-cart-button"
                  depressed
                  height="32px"
                  width="34px"
                  elevation="0"
                  :outlined="isDisabled(config, discount)"
                  :disabled="isDisabled(config, discount)"
                  @click="add(config, discount)"
                >
                  <v-icon size="10px">$plus</v-icon>
                </v-btn>
              </v-col>
              <v-col v-else cols="auto" class="d-flex align-center">
                <span class="sold-out">
                  {{ discountAvailabilityInfo(discount) }}
                </span>
              </v-col>
            </v-row>
            <div
              v-if="discountIndex + 1 < config.discounts.length"
              class="divider"
            ></div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-dialog
      v-model="showInfoDialog"
      fullscreen
      overlay-opacity="0.8"
      overlay-color="backgroundPrimary"
    >
      <v-card class="d-flex flex-column">
        <v-card-title class="mobile-description-header">{{
          selectedTicketConfigName
        }}</v-card-title>
        <v-divider class="mx-4"></v-divider>
        <v-card-text class="scrollable-content">
          <div
            class="my-4 mobile-description"
            v-html="$sanitize(selectedTicketConfigDescription)"
          ></div>
        </v-card-text>
        <v-card-actions>
          <v-col class="text-right">
            <v-btn @click="showInfoDialog = false">
              {{ $t('dialog.close') }}
            </v-btn>
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import BSaleEndTimer from '~/components/general/BSaleEndTimer.vue';
import {
  EVENT_STATES,
  calculateSmallerPrice,
  sortByDisplayOrder
} from '~/utils/utils';
import { ConditionalTicketValidator } from '~/utils/order/conditionals';

export default {
  name: 'TicketPriceListMobile',
  components: {
    BSaleEndTimer
  },
  props: {
    ticketConfig: {
      type: Array,
      default() {
        return [];
      }
    },
    ticketDiscounts: {
      type: Array,
      default() {
        return [];
      }
    },
    eventStatus: {
      type: String,
      default: ''
    },
    ownedTickets: {
      type: Array,
      default() {
        return [];
      }
    },
    selectedTickets: {
      type: Array,
      default() {
        return [];
      }
    },
    selectedSector: {
      type: Object,
      default() {
        return null;
      }
    },
    selectedTimeslot: {
      type: Object,
      default() {
        return null;
      }
    },
    selectedOccurrence: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      panels: this.ticketConfig.length === 1 ? 0 : null,
      showInfoDialog: false,
      selectedTicketConfigName: '',
      selectedTicketConfigDescription: '',
      eventStates: EVENT_STATES,
      validator: new ConditionalTicketValidator(),
      sortedTicketConfig: sortByDisplayOrder(this.ticketConfig)
    };
  },
  computed: {
    discountAvailable() {
      const now = moment();
      return (discount) => {
        if (!discount.id) {
          return true;
        }
        return (
          now.isSameOrAfter(moment(discount.startAt)) &&
          now.isSameOrBefore(moment(discount.endAt))
        );
      };
    },
    discountAvailabilityInfo() {
      const now = moment();
      return (discount) => {
        if (discount?.startAt && now.isBefore(moment(discount.startAt))) {
          return this.$t('cart.discountNotStarted');
        }
        if (discount?.endAt && now.isAfter(moment(discount.endAt))) {
          return this.$t('cart.discountEnded');
        }
        return '';
      };
    },
    isDisabled() {
      return (config, discount) => {
        return (
          config.discount.selectedQuantity === config.ticketsPerUser ||
          this.isCanceledEvent ||
          this.eventInDraft ||
          this.isSoldOut(config) ||
          this.isDiscountSoldOut(discount) ||
          !this.ticketConfigIsSelectable(config) ||
          this.timeslotSoldOut
        );
      };
    },
    conditionalTicketingSatisfied() {
      // Validate if the order would be valid if we added a ticket of `ticketConfig` type
      // to the combination of tickets in cart and already owned tickets
      return (ticketConfig) => {
        if (!this.configIsConditional(ticketConfig)) {
          return true;
        }
        return this.validator.canAddTicket(
          ticketConfig,
          this.ownedTickets,
          this.selectedTickets
        );
      };
    },
    isDiscountSoldOut() {
      return (discount) => {
        const foundDiscount = this.findDiscount(discount?.id);
        if (!foundDiscount) {
          return false;
        }
        return (
          foundDiscount?.availableTickets <= 0 ||
          !this.discountIsUsable(foundDiscount)
        );
      };
    },
    isCanceledEvent() {
      return this.eventStatus === this.eventStates.CANCELED;
    },
    eventInDraft() {
      return this.eventStatus === this.eventStates.DRAFT;
    },
    isSoldOut() {
      return (config) => {
        return (
          config.availableTickets <= 0 ||
          config.quantity <= 0 ||
          config.ended ||
          !config.started
        );
      };
    },
    timeslotHasLimitedCapacity() {
      return this.selectedTimeslot?.ticketConfigCapacity > 0;
    },
    ticketConfigCapacityReached() {
      if (!this.selectedTimeslot || !this.timeslotHasLimitedCapacity) {
        return false;
      }
      const selectedTicketsIds = this.getSelectedTicketsInTimeslot;
      const ticketConfigIds = [
        ...new Set([
          ...selectedTicketsIds,
          ...this.selectedTimeslot.associatedTicketConfig
        ])
      ];
      return (
        ticketConfigIds?.length === this.selectedTimeslot.ticketConfigCapacity
      );
    },
    getSelectedTicketsInTimeslot() {
      return this.selectedTickets
        .filter(
          (selectedTicket) =>
            selectedTicket.timeslotId === this.selectedTimeslot?.id
        )
        .map((selectedTicket) => selectedTicket.id);
    },
    ticketConfigIsSelectable() {
      return (ticketConfig) => {
        if (!this.ticketConfigCapacityReached) {
          return true;
        }
        const selectedTicketsIds = this.getSelectedTicketsInTimeslot;
        const ticketConfigAlreadyInTimeslot =
          this.selectedTimeslot.associatedTicketConfig.includes(
            ticketConfig.id
          );
        const ticketConfigAlreadyInCart = selectedTicketsIds.includes(
          ticketConfig.id
        );
        return ticketConfigAlreadyInTimeslot || ticketConfigAlreadyInCart;
      };
    },
    timeslotSoldOut() {
      return (
        this.selectedTimeslot?.availableTickets -
          this.getSelectedTicketsInTimeslot?.length <=
        0
      );
    }
  },
  watch: {
    // prevents previously selected panel being opened when sector changes
    selectedSector(newValue, oldValue) {
      if (newValue.id !== oldValue.id && this.ticketConfig.length > 1) {
        this.panels = null;
      }
    },
    // prevents previously selected panel being opened when timeslot changes
    selectedTimeslot(newValue, oldValue) {
      if (newValue !== oldValue && this.ticketConfig.length > 1) {
        this.panels = null;
      }
    },
    // prevents previously selected panel being opened when occurrence changes
    selectedOccurrence(newValue, oldValue) {
      if (newValue !== oldValue && this.ticketConfig.length > 1) {
        this.panels = null;
      }
    },
    /**
     * Sets the number of panels to 0 if the length of the newValue array is 1. Meaning that the only card/panel will be opened when we switch the "screen".
     * This is done to prevent the user from having to open the only card/panel manually.
     *
     * @param {Array} newValue - The new value of the array.
     */
    ticketConfig(newValue) {
      if (newValue.length === 1) {
        this.panels = 0;
      }
      this.sortedTicketConfig = sortByDisplayOrder(newValue);
    }
  },
  methods: {
    discountIsUsable(discount) {
      return discount?.availableTickets >= discount?.minQuantity;
    },
    // checks if user didn't select more tickets that they can buy at this point
    maxNotReached(tc) {
      return tc.ticketsPerUser - tc.userHolds - tc.amountOfTicketInCart > 0;
    },
    ticketAvailabilityLevel(ticketConfig) {
      if (
        this.isCanceledEvent ||
        ticketConfig.ended ||
        !this.ticketConfigIsSelectable(ticketConfig)
      ) {
        return this.$t('cart.notAvailable');
      }
      if (ticketConfig.availableTickets <= 0) {
        if (this.maxNotReached(ticketConfig)) {
          return this.$t('cart.soldOut').toUpperCase();
        } else {
          return this.$t('buyTickets.maxReached').toUpperCase();
        }
      }
      if (!ticketConfig.started) {
        return this.$t('cart.ticketSaleNotStarted');
      }
      if (!ticketConfig.started) {
        return this.$t('cart.ticketSaleNotStarted');
      }
      if (this.timeslotSoldOut) {
        return this.$t('cart.soldOut').toUpperCase();
      }
      return `${this.$t('from')} ${this.smallerPrice(ticketConfig)}`;
    },

    showTicketInfoDialog(config) {
      this.showInfoDialog = true;
      this.selectedTicketConfigName = config.name;
      this.selectedTicketConfigDescription = config.description;
    },
    add(config, discount) {
      if (this.configIsConditional(config) && !this.$auth.loggedIn) {
        this.$emit('openLogin');
        return;
      }
      if (!this.conditionalTicketingSatisfied(config)) {
        this.$notifier.showMessage({
          content: this.$t('conditional.error.message', {
            ticket: this.conditionalConfigText(config)
          }),
          type: 'error'
        });
        return;
      }

      this.$emit('add', { ticket: config, discount });
    },
    findDiscount(discountId) {
      return this.ticketDiscounts.find((td) => td.id === discountId);
    },
    smallerPrice(config) {
      return calculateSmallerPrice(config);
    },
    displaySaleEndTimer(config) {
      const endingMoment = moment(config.endSaleAt);
      const now = moment();
      const endsSoon = endingMoment.diff(now, 'days') <= 30;

      return config.showSaleEndTimer && this.configOnSale(config) && endsSoon;
    },
    configOnSale(config) {
      return config.started && !config.ended;
    },
    configIsConditional(config) {
      return config?.dependsOn?.length > 0;
    },
    conditionalConfigText(config) {
      return config?.dependsOn?.map((tc) => tc?.name).join(', ');
    }
  }
};
</script>

<style lang="scss">
.mobile-description {
  color: var(--v-textPrimary-base);
  font-size: 1.2rem;
}
.mobile-description-header {
  color: var(--v-textPrimary-base);
  font-size: 1.5rem !important;
}
.ticket-price-list-mobile {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: auto;
  p {
    color: var(--v-textPrimary-base);
  }
  .sold-out {
    color: red !important;
  }
  .v-expansion-panels {
    z-index: 0;
    display: block;
  }
  .ticket-price-item {
    p {
      font-size: 9px;
      line-height: 140%;
      font-weight: 400;
      color: var(--v-textPrimary-base);
    }
    .title {
      line-height: 200%;
      color: var(--v-textPrimary-base);
    }

    .title.sold-out {
      color: #6b6b6b;
    }

    .price {
      text-align: right;
      line-height: 200%;
      color: var(--v-primary-lighten3);
    }

    .price.sold-out {
      color: red;
    }
  }
  .theme--light.v-expansion-panels .v-expansion-panel {
    background-color: var(--v-backgroundPrimary-lighten5);
    border-width: 1px;
    border-radius: 8px;
    border-style: solid;
    border-color: var(--v-borderPrimary-lighten4);
  }
  .v-expansion-panel:before {
    box-shadow:
      0 0 0 0 rgba(0, 0, 0, 0.2),
      0 0 0 0 rgba(0, 0, 0, 0.14),
      0 0 0 0 rgba(0, 0, 0, 0.12) !important;
  }
  .v-expansion-panel-header {
    padding: 16px;
  }
  .v-expansion-panel-content {
    padding: 16px;
    border-top: 1px solid var(--v-borderPrimary-lighten4);
    background-color: var(--v-backgroundPrimary-lighten5);
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    .v-btn:not(.v-btn--round).v-size--default {
      padding: 0;
      min-width: 0;
      border-radius: 0;
    }
    .theme--light.v-btn.v-btn--has-bg {
      background-color: var(--v-BAMGrey-darken3);
    }
  }
  .selected-quantity {
    width: 34px;
  }
  .divider {
    margin-top: 12px;
    margin-bottom: 12px;
    border-top: 1px solid var(--v-borderPrimary-lighten4);
    border-bottom: 0;
    width: 100%;
  }
  .theme--light.v-btn.v-btn--disabled.add-cart-button {
    background-color: var(--v-buttonGray-lighten1) !important;
  }
  // if you need custom colors for the +/- button on mobile web view
  // .theme--dark.v-btn:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined).add-cart-button {
  //   background-color: var(--v-success-darken1) !important;
  //   color: var(--v-BAMGrey-base);
  // }
  // .theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined).add-cart-button {
  //   background-color: var(--v-primary-lighten3) !important;
  //   color: var(--v-BAMGrey-base);
  //   border: 0 !important;
  // }
  .theme--light.v-btn.v-btn--disabled.add-cart-button .v-icon {
    color: var(--v-textPrimary-base) !important;
  }
  .icon-styling {
    color: var(--v-backgroundPrimary-base) !important;
  }
  .sale-timer {
    border-radius: 4px 4px 0px 0px;
    background-color: var(--v-backgroundHighlightWarning-base);
    text-align: center;
  }
  .conditional-box {
    color: var(--v-textPrimary-lighten5);
    border-radius: 4px 4px 0px 0px;
    background-color: var(--v-backgroundPrimary-base);
    text-align: center;
  }
  .conditional-box > span {
    color: var(--v-textPrimary-lighten5);
  }
}
.scrollable-content {
  flex-grow: 1;
  overflow-y: auto;
  height: 464px;
}
</style>
